import { useState } from "react";
import {
  Download,
  BookOpen,
  Info,
  List,
  AlertTriangle,
  ChevronDown,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const replaceScenarioIpPlaceholders = (value) => {
  if (typeof value !== "string") return value;
  return value.replace(/\b(?:localhost|127\.0\.0\.1)\b/g, "SCENARIO_IP");
};

export default function LabWalkthroughTab({
  isLoadingScenario,
  walkthroughMarkdown,
  handleDownloadPDF,
  walkthroughRef,
}) {
  const [isEnvNoteOpen, setIsEnvNoteOpen] = useState(true);
  if (isLoadingScenario) {
    return (
      <div className="space-y-4 p-2">
        <div className="h-7 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-48"></div>
        <div className="h-4 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-full"></div>
        <div className="h-4 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-5/6"></div>
        <div className="h-4 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-4/6"></div>
        <div className="h-32 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-full mt-2"></div>
        <div className="h-4 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-full"></div>
        <div className="h-4 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-3/4"></div>
      </div>
    );
  }

  if (!walkthroughMarkdown) {
    return (
      <div className="text-center py-12 text-(--color-text-muted) dark:text-gray-400">
        <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="text-base font-semibold text-(--color-text-primary) mb-1">
          No Walkthrough Available
        </p>
        <p className="text-sm">
          No walkthrough has been added for this lab yet.
        </p>
      </div>
    );
  }

  const sanitizedWalkthrough =
    replaceScenarioIpPlaceholders(walkthroughMarkdown);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6 pb-5 border-b border-(--color-border)">
        <div>
          <h2 className="text-lg font-bold text-(--color-text-primary)">
            Step-by-Step Guide
          </h2>
          <p className="text-sm text-(--color-text-muted) dark:text-gray-400 mt-1">
            Follow these instructions to complete the laboratory assessment.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDownloadPDF}
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-(--color-primary) text-(--color-primary) font-semibold text-sm hover:bg-(--color-primary)/10 transition-all"
        >
          <Download className="h-4 w-4" />
          Download PDF
        </button>
      </div>

      <div className="mb-6 rounded-md border border-(--color-border) bg-transparent">
        <button
          type="button"
          onClick={() => setIsEnvNoteOpen((prev) => !prev)}
          className="flex w-full items-center justify-between gap-3 px-4 py-3"
          aria-expanded={isEnvNoteOpen}
        >
          <div className="flex items-center gap-2 text-md font-semibold text-(--color-text-primary) bg-transparent">
            <Info className="h-4 w-4" />
            Environment Note
          </div>
          <div className="flex items-center gap-2">
            <ChevronDown
              className={`h-4 w-4 text-(--color-text-muted) transition-transform ${
                isEnvNoteOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        </button>
        {isEnvNoteOpen && (
          <div className="space-y-3 px-4 py-4">
            <p className="text-sm text-(--color-text-primary)">
              The attack box includes the following base OT libraries by
              default:
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                "pymodbus==3.12.1",
                "cpppo==5.2.5",
                "python-snap7==2.1.0",
                "asyncua==1.1.8",
                "pydnp3-stepfunc==1.6.0.10",
              ].map((item) => {
                const [name, version] = item.split("==");
                return (
                  <div
                    key={item}
                    className="min-w-[100px] rounded-md border border-(--color-border) bg-transparent px-3 py-2 text-xs"
                  >
                    <div className="font-semibold text-(--color-text-primary)">
                      {name}
                    </div>
                    <div className="text-(--color-text-muted)">{version}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-start gap-2 rounded-md border border-yellow-500/40 bg-transparent px-3 py-2 text-sm text-yellow-700 dark:text-yellow-200">
              <AlertTriangle className="mt-0.5 h-4 w-4" />
              <p>
                Library versions may vary slightly. Some examples may need minor
                syntax or API adjustments. If you encounter an error, check the
                installed library version on the attack box and adapt the
                example accordingly. All scripts should be run using python3
                (e.g., python3 script.py).
              </p>
            </div>
          </div>
        )}
      </div>

      <div ref={walkthroughRef} className="max-w-none space-y-4">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <h1 className="text-2xl font-bold text-(--color-text-primary) mt-0 mb-4 pb-2 border-b border-(--color-border)">
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-lg font-bold text-(--color-text-primary) mt-6 mb-3 pb-2 border-b border-(--color-border)">
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-base font-bold text-(--color-text-primary) mt-4 mb-2">
                {children}
              </h3>
            ),
            h4: ({ children }) => (
              <h4 className="text-sm font-bold text-(--color-text-primary) mt-3 mb-1">
                {children}
              </h4>
            ),
            p: ({ children }) => (
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed my-2">
                {children}
              </p>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold text-(--color-text-primary)">
                {children}
              </strong>
            ),
            em: ({ children }) => (
              <em className="italic text-gray-600 dark:text-gray-400">
                {children}
              </em>
            ),
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-(--color-primary)/50 bg-(--color-primary)/5 px-4 py-2 my-3 rounded-r-lg text-gray-600 dark:text-gray-400 text-sm">
                {children}
              </blockquote>
            ),
            // FIX: react-markdown v8+ no longer passes an `inline` prop to the
            // code renderer. Instead, fenced/block code blocks are always given
            // a `className` like "language-xxx", while truly inline backtick
            // code has no className at all. Use that to distinguish the two.
            code: ({ className, children, ...props }) => {
              const isBlock = Boolean(className);
              return isBlock ? (
                <code className="block" {...props}>
                  {children}
                </code>
              ) : (
                <code
                  className="text-(--color-primary) bg-(--color-primary)/10 px-1.5 py-0.5 rounded text-xs font-mono"
                  {...props}
                >
                  {children}
                </code>
              );
            },
            pre: ({ children }) => (
              <pre className="bg-gray-900 dark:bg-black border border-(--color-border) rounded-md p-4 overflow-x-auto text-gray-100 text-xs leading-relaxed my-3">
                {children}
              </pre>
            ),
            ul: ({ children }) => (
              <ul className="list-disc list-outside space-y-1 my-2 text-sm text-gray-700 dark:text-gray-300 pl-5">
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal list-outside space-y-1 my-2 text-sm text-gray-700 dark:text-gray-300 pl-5">
                {children}
              </ol>
            ),
            li: ({ children }) => (
              <li className="leading-relaxed">{children}</li>
            ),
            hr: () => <hr className="border-(--color-border) my-6" />,
            table: ({ children }) => (
              <div className="overflow-x-auto my-4">
                <table className="w-full text-sm border-collapse">
                  {children}
                </table>
              </div>
            ),
            thead: ({ children }) => (
              <thead className="bg-(--color-primary)/10">{children}</thead>
            ),
            th: ({ children }) => (
              <th className="border border-(--color-border) px-3 py-2 text-left font-semibold text-(--color-text-primary)">
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="border border-(--color-border) px-3 py-2 text-gray-700 dark:text-gray-300">
                {children}
              </td>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-(--color-primary) underline underline-offset-2 hover:opacity-80"
              >
                {children}
              </a>
            ),
          }}
        >
          {sanitizedWalkthrough}
        </ReactMarkdown>
      </div>
    </div>
  );
}
